import { useMemo } from 'react';
import {
  Flex,
  Grid,
  Select,
  Switch,
  Text,
  TextAreaField,
  TextField,
} from '@backstage/ui';
import { useTeamOwnerFilter } from '../../../../hooks';
import { buildEntityOwnerSections } from '../../../filters';
import { selectedKey } from '../../../select-helpers';
import { MAX_NAME_LENGTH } from '../lib';
import { FieldError } from './field-error';

export type TrackDetailsSectionProps = {
  name: string;
  onNameChange: (value: string) => void;
  id: string;
  onIdChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  ownerEntityRef: string;
  onOwnerEntityRefChange: (value: string) => void;
  documentationUrl: string;
  onDocumentationUrlChange: (value: string) => void;
  inactive: boolean;
  onInactiveChange: (value: boolean) => void;
  isReadOnly?: boolean;
  nameError?: string;
  idFormatError?: string;
  idFieldError?: string;
  ownerError?: string;
  documentationUrlError?: string;
};

/** Name, description, owner, and documentation fields for a track. */
export const TrackDetailsSection = ({
  name,
  onNameChange,
  id,
  onIdChange,
  description,
  onDescriptionChange,
  ownerEntityRef,
  onOwnerEntityRefChange,
  documentationUrl,
  onDocumentationUrlChange,
  inactive,
  onInactiveChange,
  isReadOnly = false,
  nameError,
  idFormatError,
  idFieldError,
  ownerError,
  documentationUrlError,
}: TrackDetailsSectionProps) => {
  const { teamOptions, groupOptions } = useTeamOwnerFilter();

  const ownerOptions = useMemo(() => {
    const knownRef =
      teamOptions.some(option => option.value === ownerEntityRef) ||
      groupOptions.some(option => option.value === ownerEntityRef);
    const allGroups =
      ownerEntityRef && !knownRef
        ? [...groupOptions, { value: ownerEntityRef, label: ownerEntityRef }]
        : groupOptions;
    return buildEntityOwnerSections(
      teamOptions,
      allGroups,
      ownerEntityRef ? [ownerEntityRef] : [],
    );
  }, [teamOptions, groupOptions, ownerEntityRef]);

  return (
    <Flex direction="column" gap="3">
      <Grid.Root columns={{ initial: '1', sm: '2' }} gap="4">
        <TextField
          label="Name"
          value={name}
          description="A human-readable name for the track."
          maxLength={MAX_NAME_LENGTH}
          isInvalid={Boolean(nameError)}
          onChange={onNameChange}
          isDisabled={isReadOnly}
        />
        <TextField
          label="ID (kebab-case)"
          value={id}
          onChange={onIdChange}
          isDisabled={isReadOnly}
          description={
            idFormatError ??
            (isReadOnly
              ? 'The id cannot be changed after creation.'
              : 'Auto-filled from the name; you can edit it.')
          }
          isInvalid={Boolean(idFieldError)}
        />
      </Grid.Root>
      <FieldError message={nameError} />
      <FieldError message={idFieldError} />

      <TextAreaField
        label="Description"
        value={description}
        description="Explain what this track measures and why it matters."
        onChange={onDescriptionChange}
        isDisabled={isReadOnly}
        rows={3}
      />

      <Flex direction="column" gap="1">
        <Select
          label="Owner"
          options={ownerOptions}
          value={ownerEntityRef}
          onChange={keys => onOwnerEntityRefChange(selectedKey(keys) ?? '')}
          isDisabled={isReadOnly}
          isInvalid={Boolean(ownerError)}
          description={
            ownerOptions.length === 0 ? 'No groups available' : 'Select a group'
          }
        />
        <FieldError message={ownerError} />
      </Flex>

      <Flex direction="column" gap="1">
        <TextField
          label="Documentation URL"
          value={documentationUrl}
          description="Optional link to guidance for teams working through this track."
          isInvalid={Boolean(documentationUrlError)}
          onChange={onDocumentationUrlChange}
          isDisabled={isReadOnly}
        />
        <FieldError message={documentationUrlError} />
      </Flex>

      <Flex direction="column" gap="1">
        <Switch
          label="Mark track as active"
          isSelected={!inactive}
          onChange={isActive => onInactiveChange(!isActive)}
          isDisabled={isReadOnly}
        />
        <Text variant="body-small" color="secondary">
          Marking a track as inactive will hide it from the Pulse tab for an
          individual entity. It will remain visible in the Tracks view but will
          be marked as inactive.
        </Text>
      </Flex>
    </Flex>
  );
};
